import React from 'react';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DiamondOutlinedIcon from '@mui/icons-material/DiamondOutlined';
import HealingOutlinedIcon from '@mui/icons-material/HealingOutlined';
import CheckroomOutlinedIcon from '@mui/icons-material/CheckroomOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { ItemCategory } from '@prisma/client';
import { CatalogPickerModel } from '../../../hooks/checkouts/useCatalogPicker';
import { NO_BRAND_ID } from '../../../utils/catalog/catalogFilters';
import { getTextForCategory } from '../../../helpers/items';
import DirectoryRow from './DirectoryRow';

export type DirectoryLevel = 'categories' | 'category' | 'brands' | 'types';

const CATEGORY_ICONS: Record<ItemCategory, React.ReactNode> = {
  JEWELRY: <DiamondOutlinedIcon fontSize="small" />,
  AFTERCARE: <HealingOutlinedIcon fontSize="small" />,
  MERCH: <CheckroomOutlinedIcon fontSize="small" />,
  GIFT_CARD: <CardGiftcardIcon fontSize="small" />,
  OTHER: <CategoryOutlinedIcon fontSize="small" />,
};

const initials = (name: string) => (
  <Typography variant="caption" fontWeight={700}>
    {name.slice(0, 2).toUpperCase()}
  </Typography>
);

interface Props {
  picker: CatalogPickerModel;
  level: DirectoryLevel;
  onLevel: (level: DirectoryLevel) => void;
  /** Called when a selection changes so the right pane can leave a product. */
  onNavigate: () => void;
}

/** Folder-style navigation: Categories → Brands / Types → the filtered list. */
export default function CatalogDirectory({
  picker,
  level,
  onLevel,
  onNavigate,
}: Props) {
  const { selection } = picker;
  const selectedBrand = picker.brands.find(b => b.key === selection.brandId);
  const categoryName = selection.category
    ? getTextForCategory(selection.category)
    : '';

  // both words share one Typography variant so they sit on the same line
  const header = (title: string, back: () => void) => (
    <Box display="flex" alignItems="center" gap={1}>
      <ButtonBase
        onClick={back}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.5,
          borderRadius: '6px',
          color: theme => theme.palette.primary.main,
        }}>
        <ArrowBackRoundedIcon fontSize="small" />
        <Typography variant="subtitle2" fontWeight={600} color="inherit">
          Back
        </Typography>
      </ButtonBase>
      <Typography variant="subtitle2" color="text.secondary" noWrap>
        {title}
      </Typography>
    </Box>
  );

  if (level === 'categories' || !selection.category) {
    return (
      <Stack gap={0.25}>
        <Typography variant="overline" color="text.secondary" px={1.25}>
          Categories
        </Typography>
        {picker.categories.map(tile => (
          <DirectoryRow
            key={tile.key}
            label={getTextForCategory(tile.key as ItemCategory)}
            count={tile.count}
            leading={CATEGORY_ICONS[tile.key as ItemCategory]}
            folder
            onClick={() => {
              picker.selectCategory(tile.key as ItemCategory);
              onLevel('category');
              onNavigate();
            }}
          />
        ))}
      </Stack>
    );
  }

  if (level === 'brands') {
    return (
      <Stack gap={0.25}>
        {header(`${categoryName} · Brands`, () => onLevel('category'))}
        <DirectoryRow
          label="All brands"
          selected={!selection.brandId}
          onClick={() => {
            picker.toggleBrand(selection.brandId ?? '');
            onLevel('category');
            onNavigate();
          }}
        />
        {picker.brands.map(brand => (
          <DirectoryRow
            key={brand.key}
            label={brand.label}
            count={brand.count}
            imageUrl={brand.imageUrl}
            leading={
              brand.key === NO_BRAND_ID ? (
                <FolderOutlinedIcon fontSize="small" />
              ) : (
                initials(brand.label)
              )
            }
            selected={selection.brandId === brand.key}
            onClick={() => {
              if (selection.brandId !== brand.key)
                picker.toggleBrand(brand.key);
              onLevel('category');
              onNavigate();
            }}
          />
        ))}
      </Stack>
    );
  }

  if (level === 'types') {
    return (
      <Stack gap={0.25}>
        {header(`${categoryName} · Types`, () => onLevel('category'))}
        <DirectoryRow
          label="All types"
          selected={!selection.typeLabel}
          onClick={() => {
            picker.toggleType(selection.typeLabel ?? '');
            onLevel('category');
            onNavigate();
          }}
        />
        {picker.types.map(type => (
          <DirectoryRow
            key={type.key}
            label={type.label}
            count={type.count}
            imageUrl={type.imageUrl}
            leading={initials(type.label)}
            selected={selection.typeLabel === type.key}
            onClick={() => {
              if (selection.typeLabel !== type.key) picker.toggleType(type.key);
              onLevel('category');
              onNavigate();
            }}
          />
        ))}
      </Stack>
    );
  }

  const showBrands = picker.brands.length > 1 || !!selection.brandId;
  const showTypes = picker.types.length > 1 || !!selection.typeLabel;

  return (
    <Stack gap={0.25}>
      {header(categoryName, () => {
        picker.selectCategory(null);
        onLevel('categories');
        onNavigate();
      })}
      {showBrands && (
        <DirectoryRow
          label="Brand"
          caption={
            selectedBrand?.label ?? `All · ${picker.brands.length} brands`
          }
          leading={<FolderOutlinedIcon fontSize="small" />}
          folder
          onClick={() => onLevel('brands')}
        />
      )}
      {showTypes && (
        <DirectoryRow
          label="Type"
          caption={selection.typeLabel ?? `All · ${picker.types.length} types`}
          leading={<FolderOutlinedIcon fontSize="small" />}
          folder
          onClick={() => onLevel('types')}
        />
      )}
      <Typography variant="caption" color="text.secondary" px={1.25} pt={1}>
        {picker.products.length} item{picker.products.length === 1 ? '' : 's'}
      </Typography>
    </Stack>
  );
}
